import { useLazyQuery } from '@apollo/client';
import { useSnackbar } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FETCH_PORTFOLIO_REPORTS } from '../queries';
import { useTradingHub } from '../tradingHubContext';
import type { PortfolioReport } from '../types';
import { buildPartialPortfolioReportFromPin } from '../utils/buildPartialPortfolioReportFromPin';
import { enrichPortfolioReport } from '../utils/enrichPortfolioReport';
import type { EnrichedPortfolioReport } from './usePortfolioTableState.types';

const MAX_SCROLL_RETRY_ATTEMPTS = 15;
const SCROLL_RETRY_INTERVAL_MS = 120;

export function usePortfolioDeepLink({
  organizationId,
  portfolios,
  canReadRepCode,
}: {
  organizationId?: string;
  portfolios: PortfolioReport[];
  canReadRepCode: boolean;
}) {
  const { pinnedPortfolioDeepLink, setPinnedPortfolioDeepLink } =
    useTradingHub();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('portfolioTradingHub');

  const [expandedPortfolioId, setExpandedPortfolioId] = useState<string | null>(
    null,
  );

  const scrolledPinRef = useRef<string | null>(null);
  const fetchedPinRef = useRef<string | null>(null);
  const pinnedHydrationWarnedRef = useRef<string | null>(null);

  const [fetchPinnedReport, { data: pinnedReportData }] = useLazyQuery(
    FETCH_PORTFOLIO_REPORTS,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  );

  const dismissPinnedDeepLink = useCallback(() => {
    setPinnedPortfolioDeepLink(null);
  }, [setPinnedPortfolioDeepLink]);

  const handleSetExpanded = useCallback(
    (id: string | null) => {
      setExpandedPortfolioId(id);
      if (
        pinnedPortfolioDeepLink &&
        id !== pinnedPortfolioDeepLink.portfolioReportId
      ) {
        dismissPinnedDeepLink();
      }
    },
    [pinnedPortfolioDeepLink, dismissPinnedDeepLink],
  );

  useEffect(() => {
    if (!pinnedPortfolioDeepLink) return;
    setExpandedPortfolioId(pinnedPortfolioDeepLink.portfolioReportId);
  }, [pinnedPortfolioDeepLink]);

  useEffect(() => {
    if (!pinnedPortfolioDeepLink || !organizationId) {
      fetchedPinRef.current = null;
      return;
    }
    const pinId = pinnedPortfolioDeepLink.portfolioReportId;
    if (portfolios.some((p) => p.id === pinId)) {
      fetchedPinRef.current = null;
      return;
    }
    if (fetchedPinRef.current === pinId) return;

    const pinned = pinnedPortfolioDeepLink.report;
    const objectId = pinned.object?.id;
    if (!objectId) {
      if (pinnedHydrationWarnedRef.current !== pinId) {
        pinnedHydrationWarnedRef.current = pinId;
        enqueueSnackbar(t('deepLink.missingObjectId'), { variant: 'warning' });
      }
      return;
    }
    pinnedHydrationWarnedRef.current = null;

    fetchedPinRef.current = pinId;
    const isGoal = pinned.modelPortfolioType === 'GOAL';
    fetchPinnedReport({
      variables: {
        input: {
          filter: {
            organizationId,
            ...(isGoal ? { goalId: objectId } : { subAccountId: objectId }),
          },
          pagination: { perPage: 1, offSet: 0 },
        },
        canReadRepCode,
      },
    }).catch(() => {
      fetchedPinRef.current = null;
    });
  }, [
    pinnedPortfolioDeepLink,
    organizationId,
    portfolios,
    fetchPinnedReport,
    enqueueSnackbar,
    t,
    canReadRepCode,
  ]);

  const hydratedPinnedReport = useMemo(() => {
    if (!pinnedPortfolioDeepLink) return null;
    const expectedId = pinnedPortfolioDeepLink.portfolioReportId;
    const rows = pinnedReportData?.fetchPortfolioReports?.portfolioReports;
    if (!rows?.length) return null;
    const match = rows.find(
      (row: PortfolioReport | null) => row?.id === expectedId,
    );
    return (match ?? null) as PortfolioReport | null;
  }, [pinnedReportData, pinnedPortfolioDeepLink]);

  const ghostReport = useMemo((): EnrichedPortfolioReport | null => {
    if (!pinnedPortfolioDeepLink) return null;
    const pinned = pinnedPortfolioDeepLink.report;
    if (expandedPortfolioId !== pinnedPortfolioDeepLink.portfolioReportId) {
      return null;
    }
    if (portfolios.some((p) => p.id === pinned.id)) return null;

    const reportForPriority =
      hydratedPinnedReport ?? buildPartialPortfolioReportFromPin(pinned);

    return enrichPortfolioReport(reportForPriority);
  }, [
    pinnedPortfolioDeepLink,
    expandedPortfolioId,
    portfolios,
    hydratedPinnedReport,
  ]);

  useEffect(() => {
    if (!pinnedPortfolioDeepLink) {
      scrolledPinRef.current = null;
      return;
    }
    const targetId = pinnedPortfolioDeepLink.portfolioReportId;
    if (scrolledPinRef.current === targetId) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tryScroll = () => {
      const el = document.getElementById(`portfolio-row-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrolledPinRef.current = targetId;
      } else if (attempts < MAX_SCROLL_RETRY_ATTEMPTS) {
        attempts++;
        timer = setTimeout(tryScroll, SCROLL_RETRY_INTERVAL_MS);
      }
    };
    timer = setTimeout(tryScroll, SCROLL_RETRY_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [pinnedPortfolioDeepLink]);

  return {
    expandedPortfolioId,
    handleSetExpanded,
    ghostReport,
    pinnedPortfolioReportId: pinnedPortfolioDeepLink?.portfolioReportId,
  };
}
